import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  TextField,
  Checkbox,
  FormControlLabel,
  Paper,
} from '@mui/material';
import { toast } from 'react-toastify';
import { ChangeLegTime } from 'src/queries/queriess';

const ChangeTimesModal = ({ open, handleClose, leg, refetchOrders }) => {
  const [changeOrderTimes, setChangeOrderTimes] = useState(false);

  // Parse existing times for form initialization
  const parseDateTime = (dateTime) => {
    if (!dateTime) return { dateString: "", timeString: "" };
    const date = new Date(dateTime);
    const dateString = date.toISOString().split("T")[0];
    const timeString = date.toISOString().split("T")[1].split(".")[0].substring(0, 5);
    return { dateString, timeString };
  };

  // Form state
  const [formData, setFormData] = useState({
    ready_date: parseDateTime(leg?.readyTimeStart).dateString || '',
    ready_start_time: parseDateTime(leg?.readyTimeStart).timeString || '00:00',
    ready_end_time: parseDateTime(leg?.readyTimeEnd).timeString || '00:00',
    requested_date: parseDateTime(leg?.requestedTimeStart).dateString || '',
    requested_start_time: parseDateTime(leg?.requestedTimeStart).timeString || '',
    requested_end_time: parseDateTime(leg?.requestedTimeEnd).timeString || '',
    pickup_date: parseDateTime(leg?.pickUpTimeStart).dateString || '',
    pickup_start_time: parseDateTime(leg?.pickUpTimeStart).timeString || '',
    pickup_end_time: parseDateTime(leg?.pickUpTimeEnd).timeString || '',
    delivery_date: parseDateTime(leg?.deliveryTimeStart).dateString || '',
    delivery_start_time: parseDateTime(leg?.deliveryTimeStart).timeString || '',
    delivery_end_time: parseDateTime(leg?.deliveryTimeEnd).timeString || '',
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleClearDate = (field) => {
    setFormData(prev => ({ ...prev, [field]: '' }));
  };

  const handleNowButton = (type) => {
    const now = new Date();
    const dateString = now.toISOString().split('T')[0];
    const timeString = now.toISOString().split('T')[1].split('.')[0].substring(0, 5);
    
    if (type === 'pickup') {
      setFormData(prev => ({
        ...prev,
        pickup_date: dateString,
        pickup_start_time: timeString
      }));
    } else if (type === 'delivery') {
      setFormData(prev => ({
        ...prev,
        delivery_date: dateString,
        delivery_start_time: timeString
      }));
    }
  };

  const handleSubmit = async () => {
    try {
      const formattedValues = {
        readyTimeStart: formData.ready_date && formData.ready_start_time 
          ? `${formData.ready_date}T${formData.ready_start_time}:00Z` 
          : leg?.readyTimeStart,
        readyTimeEnd: formData.ready_date && formData.ready_end_time 
          ? `${formData.ready_date}T${formData.ready_end_time}:00Z` 
          : leg?.readyTimeEnd,
        requestedTimeStart: formData.requested_start_time 
          ? `${formData.ready_date || parseDateTime(leg?.requestedTimeStart).dateString}T${formData.requested_start_time}:00Z` 
          : leg?.requestedTimeStart,
        requestedTimeEnd: formData.requested_end_time 
          ? `${formData.ready_date || parseDateTime(leg?.requestedTimeStart).dateString}T${formData.requested_end_time}:00Z` 
          : leg?.requestedTimeEnd,
        pickUpTimeStart: formData.pickup_date && formData.pickup_start_time 
          ? `${formData.pickup_date}T${formData.pickup_start_time}:00Z` 
          : leg?.pickUpTimeStart,
        pickUpTimeEnd: formData.pickup_date && formData.pickup_end_time 
          ? `${formData.pickup_date}T${formData.pickup_end_time}:00Z` 
          : leg?.pickUpTimeEnd,
        deliveryTimeStart: formData.delivery_date && formData.delivery_start_time 
          ? `${formData.delivery_date}T${formData.delivery_start_time}:00Z` 
          : leg?.deliveryTimeStart,
        deliveryTimeEnd: formData.delivery_date && formData.delivery_end_time 
          ? `${formData.delivery_date}T${formData.delivery_end_time}:00Z` 
          : leg?.deliveryTimeEnd,
      };

      const result = await ChangeLegTime({ ...formattedValues, uuid: leg?.uuid });
      
      if (result.status === 200) {
        toast.success('Times updated successfully!');
        handleClose();
        if (refetchOrders) {
          refetchOrders();
        }
      } else {
        toast.error('Failed to update times');
      }
    } catch (error) {
      console.error('Error updating times:', error);
      toast.error('Error updating times');
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Typography variant="h6">Change Times</Typography>
        <Box>
          <Button onClick={handleClose} sx={{ mr: 1 }}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            Save
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2, pb: 1 }}>
        <Grid container spacing={3}>
          {/* First Row: READY TIME + REQUESTED DELIVERY */}
          <Grid item xs={12} md={6}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>READY TIME</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  type="date"
                  variant='outlined'
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={formData.ready_date}
                  onChange={(e) => handleInputChange('ready_date', e.target.value)}
                  sx={{ width: 150 }}
                />
                <Button onClick={() => handleClearDate('ready_date')} size="small" variant="outlined" sx={{ minWidth: 60 }}>
                  Clear
                </Button>
                <TextField
                  label="START"
                  type="time"
                  size="small"
                  variant='outlined'
                  InputLabelProps={{ shrink: true }}
                  value={formData.ready_start_time}
                  onChange={(e) => handleInputChange('ready_start_time', e.target.value)}
                  sx={{ width: 120 }}
                />
                <TextField
                  label="END"
                  type="time"
                  size="small"
                  variant='outlined'
                  InputLabelProps={{ shrink: true }}
                  value={formData.ready_end_time}
                  onChange={(e) => handleInputChange('ready_end_time', e.target.value)}
                  sx={{ width: 120 }}
                />
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>REQUESTED DELIVERY</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  type="date"
                  size="small"
                    InputLabelProps={{ shrink: true }}
variant='outlined'
                  value={formData.requested_date}
                  onChange={(e) => handleInputChange('requested_date', e.target.value)}
                  sx={{ width: 150 }}
                />
                <Button onClick={() => handleClearDate('requested_date')} size="small" variant="outlined" sx={{ minWidth: 60 }}>
                  Clear
                </Button>
                <TextField
                  label="START"
                  type="time"
                  size="small"
                  variant='outlined'
                  InputLabelProps={{ shrink: true }}
                  value={formData.requested_start_time}
                  onChange={(e) => handleInputChange('requested_start_time', e.target.value)}
                  sx={{ width: 120 }}
                />
                <TextField
                  label="END"
                  type="time"
                  size="small"
                  variant='outlined'
                  InputLabelProps={{ shrink: true }}
                  value={formData.requested_end_time}
                  onChange={(e) => handleInputChange('requested_end_time', e.target.value)}
                  sx={{ width: 120 }}
                />
              </Box>
            </Box>
          </Grid>

          {/* Second Row: PICKUP APPOINTMENT + DELIVERY APPOINTMENT */}
          <Grid item xs={12} md={6}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>PICKUP APPOINTMENT</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  type="date"
                  size="small"
                  variant='outlined'
                  InputLabelProps={{ shrink: true }}
                  value={formData.pickup_date}
                  onChange={(e) => handleInputChange('pickup_date', e.target.value)}
                  sx={{ width: 150 }}
                />
                <Button onClick={() => handleNowButton('pickup')} size="small" variant="outlined" sx={{ minWidth: 60 }}>
                  Now
                </Button>
                <TextField
                  label="Start"
                  type="time"
                  size="small"
                  variant='outlined'
                  InputLabelProps={{ shrink: true }}
                  value={formData.pickup_start_time}
                  onChange={(e) => handleInputChange('pickup_start_time', e.target.value)}
                  sx={{ width: 120 }}
                />
                <TextField
                  label="End"
                  type="time"
                  size="small"
                  variant='outlined'
                  InputLabelProps={{ shrink: true }}
                  value={formData.pickup_end_time}
                  onChange={(e) => handleInputChange('pickup_end_time', e.target.value)}
                  sx={{ width: 120 }}
                />
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>DELIVERY APPOINTMENT</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  type="date"
                  size="small"
                  variant='outlined'
                  InputLabelProps={{ shrink: true }}
                  value={formData.delivery_date}
                  onChange={(e) => handleInputChange('delivery_date', e.target.value)}
                  sx={{ width: 150 }}
                />
                <Button onClick={() => handleNowButton('delivery')} size="small" variant="outlined" sx={{ minWidth: 60 }}>
                  Now
                </Button>
                <TextField
                  label="Start"
                  type="time"
                  size="small"
                  variant='outlined'
                  InputLabelProps={{ shrink: true }}
                  value={formData.delivery_start_time}
                  onChange={(e) => handleInputChange('delivery_start_time', e.target.value)}
                  sx={{ width: 120 }}
                />
                <TextField
                  label="End"
                  type="time"
                  size="small"
                  variant='outlined'
                  InputLabelProps={{ shrink: true }}
                  value={formData.delivery_end_time}
                  onChange={(e) => handleInputChange('delivery_end_time', e.target.value)}
                  sx={{ width: 120 }}
                />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'flex-start' }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={changeOrderTimes}
              onChange={(e) => setChangeOrderTimes(e.target.checked)}
            />
          }
          label="Change order times"
        />
      </DialogActions>
    </Dialog>
  );
};

export default ChangeTimesModal;
